import { useCallback, useMemo } from 'react';

import { useSelector } from 'react-redux';

import AvatarGroup from '@/components/atoms/Avatar/AvatarGroup/AvatarGroup';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useRtmViewing } from '@/hooks/rtm/useRtmViewing';
import useMembers from '@/hooks/useMembers';
import useUser from '@/hooks/useUser';
import { selectLast3ViewersBySessionId } from '@/redux/presence/selectors';
import { selectSessionId } from '@/redux/session/selectors';
import { RootState } from '@/shared/types/state';

export const ConversationViewing = () => {
  useRtmViewing();
  const sessionId = useSelector(selectSessionId);
  const { user } = useUser();
  const { getMember } = useMembers();

  const { viewers, overflow, count } = useSelector((state: RootState) =>
    selectLast3ViewersBySessionId(state, sessionId, user?.user_id)
  );

  const overflowNames = useMemo(
    () =>
      overflow
        .map(({ user_id }) => getMember(user_id)?.name)
        .filter(Boolean)
        .join(', '),
    [overflow, getMember]
  );

  const renderOverflowNode = useCallback(
    (n: number) => (
      <InfoTooltip content={overflowNames}>
        <span className="z-10 flex size-7 cursor-default items-center justify-center rounded-full border-2 border-background bg-bg-muted text-xs text-foreground-muted">
          +{n}
        </span>
      </InfoTooltip>
    ),
    [overflowNames]
  );

  if (count === 0) {
    return null;
  }

  return (
    <div className="-translate-x-1">
      <AvatarGroup max={3} total={count} overflowNode={renderOverflowNode}>
        {viewers.map(({ user_id }) => {
          const member = getMember(user_id);
          return (
            <InfoTooltip key={user_id} content={member?.name}>
              <Avatar size="xs">
                <AvatarImage src={member?.avatar} alt={member?.name} />
                <AvatarFallback size="xs">{member?.name?.[0] || '?'}</AvatarFallback>
              </Avatar>
            </InfoTooltip>
          );
        })}
      </AvatarGroup>
    </div>
  );
};
